// AVRASTRO account on this device: sign-in / registration against the AVRASTRO platform, the signed subscription
// licence (verified offline with the platform's public key), licence enforcement, and the hand-over of the
// session to the sync service. Passwords are never stored; the session token lives in the device's local
// metadata (encrypted when the device vault is on). Without an account AVRASTRO keeps working fully offline —
// unless the owner builds the app with `requireAccount`.
// 0.6 — sign-in through avrastro.com: in the browser the web app (served by the platform) exchanges the website's
// HttpOnly session cookie for a session token that is kept IN MEMORY only (re-exchanged on reload); the desktop
// app uses device authorisation (the advisor confirms a short code on the website, no password in the app).
// A workspace linked to the website keeps its signed licence enforced offline, even when signed out.
import { claimLocalBranding, brandingProfiles, saveBranding, TABLE_NAMES, isLocalTable } from '@vero/domain';
import type { Workspace } from '@vero/store';
import { PlatformClient, PlatformError, daysLeft, decodeLicence, featureAccess, licenceState, lockedFeatureForAction, platformUrl, verifyLicence, type AccountProfile, type AccountSession, type DeviceCode, type LicencePayload, type LicenceState, type LoginResponse, type MeResult, type Plan, type PlatformInfo, type RegisterInput, type SubscriptionInfo } from '@vero/platform';
import { syncService } from './sync-service.ts';

declare const __VERO_PLATFORM__: { url: string | null; publicKey: JsonWebKey | null; requireAccount: boolean } | undefined;
/** owner build configuration (tools/build.mjs: VERO_PLATFORM_URL, VERO_PLATFORM_PUBLIC_KEY, VERO_REQUIRE_ACCOUNT) */
export const PLATFORM_CONFIG: { url: string | null; publicKey: JsonWebKey | null; requireAccount: boolean } =
  (typeof __VERO_PLATFORM__ !== 'undefined' && __VERO_PLATFORM__) || { url: null, publicKey: null, requireAccount: false };

export interface AccountState {
  serverUrl: string | null; token: string | null; email: string | null; userId: string | null; role: string | null;
  workspaceId: string | null; workspaceName: string | null; licence: string | null; publicKey: JsonWebKey | null;
  checkedAt: string | null; revoked: boolean; lastError: string | null;
  profile: (AccountProfile & { email: string }) | null; plan: Plan | null; subscription: SubscriptionInfo | null; mfaEnabled: boolean;
  /** how the session was obtained (0.6): password, website (browser, token in memory only) or device (desktop) */
  via?: 'password' | 'website' | 'device' | null;
  /** the connected website (e.g. https://avrastro.com) when the platform has one */
  website?: string | null;
  /** the workspace the data on this device belongs to — kept after sign-out (tenant safety) */
  owner?: { id: string; name: string | null } | null;
}
export interface AccountUiState extends Omit<AccountState, 'token' | 'licence'> {
  signedIn: boolean; licence: LicencePayload | null; licenceState: LicenceState; daysLeft: number | null; readOnly: string | null; requireAccount: boolean;
  /** requirement 20: the signed licence decides this device's rights (signed in, or a website/device-linked workspace) */
  enforced: boolean;
}
type Listener = (s: AccountUiState) => void;
const META = 'account.state';
const EMPTY: AccountState = { serverUrl: null, token: null, email: null, userId: null, role: null, workspaceId: null, workspaceName: null, licence: null, publicKey: null, checkedAt: null, revoked: false, lastError: null, profile: null, plan: null, subscription: null, mfaEnabled: false, via: null, website: null };
const isDesktop = () => typeof globalThis !== 'undefined' && '__TAURI_INTERNALS__' in globalThis;
/** the web app is served by the platform itself (same origin): website sign-in is possible here */
export const sameOriginPlatform = (): string | null => {
  if (isDesktop() || typeof location === 'undefined' || !/^https?:$/.test(location.protocol)) return null;
  // the platform marks the page it serves (apps/platform-server, PLATFORM_APP_DIR); a hosted build may also be pinned to its own origin
  const marked = typeof document !== 'undefined' && document.querySelector('meta[name="vero-platform"][content="same-origin"]');
  return marked || (PLATFORM_CONFIG.url ?? '').replace(/\/+$/, '') === location.origin ? location.origin : null;
};
export interface DeviceSignIn { userCode: string; verificationUri: string; verificationUriComplete: string; expiresAt: number; done: Promise<AccountSession | null>; cancel(): void }
const sameKey = (a: JsonWebKey | null | undefined, b: JsonWebKey | null | undefined) => !!a && !!b && a.x === b.x && a.y === b.y && a.crv === b.crv;

export function deviceLabel(): string {
  if (typeof navigator === 'undefined') return 'AVRASTRO';
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) && !/Mobile/.test(ua) ? 'macOS' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  const app = '__TAURI_INTERNALS__' in globalThis ? 'AVRASTRO desktop' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return [app, os].filter(Boolean).join(' · ');
}

export class AccountService {
  readonly ws: Workspace;
  private s: AccountState = { ...EMPTY };
  private verified: LicencePayload | null = null;
  /** website sessions in the browser: the token is never written to storage */
  private memToken: string | null = null;
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private constructor(ws: Workspace) { this.ws = ws; }

  static async start(ws: Workspace): Promise<AccountService> {
    const a = new AccountService(ws);
    a.s = { ...EMPTY, ...((await ws.adapter.getMeta<AccountState>(META)) ?? {}) };
    await a.evaluate();
    // returning from avrastro.com (…#/now?sso=1) or a website session from an earlier visit: exchange the cookie again
    const back = typeof location !== 'undefined' && /[?&]sso=1\b/.test(location.hash);
    if (sameOriginPlatform() && (back || a.s.via === 'website')) await a.completeWebsiteSignIn(back).catch(() => undefined);
    if (a.signedIn) { void a.refresh(); a.timer = setInterval(() => void a.refresh(), 6 * 3600_000); }
    syncService()?.subscribe((st) => { if (st.lastError === 'licence_expired' || st.lastError === 'plan_feature') void a.refresh(); });
    return a;
  }
  private get token(): string | null { return this.s.token ?? this.memToken; }
  get signedIn() { return !!(this.token && this.s.serverUrl && !this.s.revoked); }
  get serverUrl() { return this.s.serverUrl ?? PLATFORM_CONFIG.url; }
  private get deviceId(): string { return syncService()?.deviceId ?? 'device'; }
  subscribe(fn: Listener) { this.listeners.add(fn); fn(this.snapshot()); return () => { this.listeners.delete(fn); }; }
  snapshot(): AccountUiState {
    const { token: _t, licence: _l, ...rest } = this.s;
    const st = this.licenceStateNow();
    return { ...rest, signedIn: this.signedIn, licence: this.verified, licenceState: st, daysLeft: this.verified ? daysLeft(st === 'grace' ? this.verified.grace : this.verified.exp) : null, readOnly: this.ws.readOnlyReason, requireAccount: PLATFORM_CONFIG.requireAccount, enforced: this.enforced };
  }
  private emit() { const x = this.snapshot(); for (const l of this.listeners) l(x); }
  private async save(patch: Partial<AccountState>) { this.s = { ...this.s, ...patch }; await this.ws.adapter.setMeta(META, this.s); }
  private client(url = this.serverUrl): PlatformClient { if (!url) throw new PlatformError(0, 'no_server'); return new PlatformClient(url, this.token); }

  /** the signed licence applies on this device (signed in, required by the build, or a website/device-linked workspace) */
  private get enforced(): boolean { return this.signedIn || PLATFORM_CONFIG.requireAccount || ((this.s.via === 'website' || this.s.via === 'device') && !!this.s.licence); }
  private licenceStateNow(): LicenceState {
    if (!this.s.licence) return 'none';
    if (!this.verified) return 'invalid';
    return licenceState(this.verified);
  }
  /** verify the stored licence offline and apply enforcement (read-only after the grace period; data is never touched) */
  private async evaluate() {
    const key = PLATFORM_CONFIG.publicKey ?? this.s.publicKey;
    let p: LicencePayload | null = null;
    if (this.s.licence && key) {
      p = await verifyLicence(this.s.licence, key);
      if (p && (p.ws !== this.s.workspaceId || p.dev !== this.deviceId)) p = null; // a licence is bound to its workspace and device
    }
    this.verified = p;
    const st = this.licenceStateNow();
    // a workspace linked through the website stays under its signed licence while signed out (offline)
    const enforced = this.enforced;
    let block: string | null = null;
    if (enforced && st === 'expired') block = 'licence_expired';
    else if (PLATFORM_CONFIG.requireAccount && (!this.signedIn || st === 'invalid')) block = 'licence_required';
    syncService()?.setLicenceBlock(block);
    // requirement 20: a premium module's records are written only with the feature in the VERIFIED licence (offline);
    // without it they stay visible read-only — nothing is hidden or deleted (entitlements.ts)
    const rights = { licence: p, state: st, enforced };
    this.ws.writeGuard = (cs) => { const f = lockedFeatureForAction(cs.action, (x) => featureAccess(x, rights).access); return f ? `plan_feature:${f}` : null; };
    this.emit();
  }

  /** any client-side business record (everything that sync would upload into a workspace) */
  private hasLocalData(): boolean {
    const d = this.ws.data as unknown as Record<string, unknown[]>;
    // every replicated table counts, except configuration that every new workspace receives anyway
    const notClientData = new Set(['settings', 'audit_events', 'automation_rules', 'automation_runs', 'branding_profiles', 'tax_rulesets', 'checklist_templates', 'daily_goals', 'plan_blocks']);
    return TABLE_NAMES.filter((t) => !isLocalTable(t) && !notClientData.has(t)).some((t) => (d[t]?.length ?? 0) > 0);
  }
  /** an AVRASTRO OS account (password) links its workspace to the advisor's avrastro.com account: one-time ticket → website sign-in */
  async linkWebsite(): Promise<string> { return (await this.api.linkWebsite()).url; }
  /** the workspace whose data this device holds (shown when another account tries to sign in) */
  get localWorkspace(): string | null { return this.s.owner?.name ?? this.s.owner?.id ?? this.s.workspaceName ?? this.s.workspaceId; }

  info(url?: string): Promise<PlatformInfo> { return this.client(url ? platformUrl(url) : undefined).info(); }

  /** adopt a fresh session: pin the platform key, verify the licence, hand the session to sync, claim the local brand */
  private async adopt(url: string, email: string, ses: AccountSession, o: { via?: AccountState['via']; ephemeral?: boolean } = {}) {
    const pinned = PLATFORM_CONFIG.publicKey ?? (this.s.serverUrl === url ? this.s.publicKey : null);
    if (pinned && !sameKey(pinned, ses.publicKey)) throw new PlatformError(0, 'key_mismatch');
    // tenant safety: data on this device that belongs to ANOTHER workspace is never uploaded into this one
    const owner = this.s.owner?.id ?? this.s.workspaceId;
    if (owner && owner !== ses.workspaceId && this.hasLocalData()) throw new PlatformError(409, 'workspace_mismatch');
    this.memToken = o.ephemeral ? ses.token : null;
    await this.save({ serverUrl: url, token: o.ephemeral ? null : ses.token, email, userId: ses.userId, role: ses.role, workspaceId: ses.workspaceId, workspaceName: ses.workspaceName, licence: ses.licence || null, publicKey: ses.publicKey, checkedAt: new Date().toISOString(), revoked: false, lastError: null, via: o.via ?? 'password', owner: { id: ses.workspaceId, name: ses.workspaceName } });
    await this.evaluate();
    if (!this.timer) this.timer = setInterval(() => void this.refresh(), 6 * 3600_000);
    if (!this.ws.readOnlyReason) {
      await this.ws.run((v, c) => claimLocalBranding(v, ses.userId, c)).catch(() => undefined);
    }
    await this.refresh();
    await this.website(url).catch(() => null);
    if (!this.ws.readOnlyReason) await this.seedBranding().catch(() => undefined);
    const lic = ses.licence ? decodeLicence(ses.licence) : null;
    const canSync = !lic || lic.features.includes('sync');
    await syncService()?.adoptSession({ serverUrl: url, token: ses.token, userId: ses.userId, role: ses.role, workspaceId: ses.workspaceId, workspaceName: ses.workspaceName, email: this.s.email || email, leaseUntil: ses.leaseUntil }, canSync, { ephemeral: !!o.ephemeral });
  }

  // --- 0.6: sign-in through avrastro.com -------------------------------------------------------------------
  /** the website connected to a platform (null: password sign-in only). 0.7: a website the platform has not heard from
   *  (plugin not installed yet) is not offered — its sign-in page would not exist. */
  async website(url?: string): Promise<string | null> {
    try { const i = await this.client(url ? platformUrl(url) : (this.serverUrl ?? sameOriginPlatform() ?? undefined)).info(); const w = i.website && i.website.connected !== false ? i.website.url : null; if (w !== (this.s.website ?? null)) await this.save({ website: w }); return w; } catch { return this.s.website ?? null; }
  }
  /** browser: go to the website to sign in; it comes back to …/#/now?sso=1 */
  startWebsiteSignIn() { const o = sameOriginPlatform(); if (!o) throw new PlatformError(0, 'website_web_only'); location.assign(`${o}/sso/start`); }
  /** browser: exchange the HttpOnly website cookie for an in-memory session */
  async completeWebsiteSignIn(clean = true): Promise<boolean> {
    const o = sameOriginPlatform(); if (!o) return false;
    if (clean && typeof history !== 'undefined') history.replaceState(null, '', location.hash.replace(/([?&])sso=1(&|$)/, (_m, a: string, b: string) => (b ? a : '')).replace(/\?$/, ''));
    try {
      const ses = await new PlatformClient(o).webExchange(this.deviceId, deviceLabel());
      await this.adopt(o, this.s.email ?? '', ses, { via: 'website', ephemeral: true });
      return true;
    } catch (e) {
      const err = e as PlatformError;
      if (err.status === 401 || err.status === 403) { this.memToken = null; if (this.s.via === 'website') await this.save({ lastError: 'website_session_ended' }); await this.evaluate(); }
      else await this.save({ lastError: err.message || 'network' });
      return false;
    }
  }
  /** desktop: device authorisation. Shows a code; the advisor confirms it on the website; the app polls. */
  async startDeviceSignIn(url: string): Promise<DeviceSignIn> {
    const u = platformUrl(url);
    const c = new PlatformClient(u);
    const dc: DeviceCode = await c.deviceCode(this.deviceId, deviceLabel());
    let stopped = false;
    const expiresAt = Date.now() + dc.expires_in * 1000;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const done = (async (): Promise<AccountSession | null> => {
      let interval = Math.max(2, dc.interval) * 1000;
      while (!stopped && Date.now() < expiresAt) {
        await wait(interval);
        if (stopped) return null;
        try {
          const ses = await c.deviceToken(dc.device_code);
          await this.adopt(u, '', ses, { via: 'device' });
          return ses;
        } catch (e) {
          const code = (e as PlatformError).message;
          if (code === 'authorization_pending' || code === 'network') continue;
          if (code === 'slow_down') { interval += 2000; continue; }
          throw e;
        }
      }
      if (!stopped) throw new PlatformError(400, 'expired_token');
      return null;
    })();
    return { userCode: dc.user_code, verificationUri: dc.verification_uri, verificationUriComplete: dc.verification_uri_complete, expiresAt, done, cancel: () => { stopped = true; } };
  }
  /** the account profile fills the advisor's document identity where it is still empty (never overwrites) */
  private async seedBranding() {
    const p = this.s.profile; if (!p || !this.s.userId) return;
    const mine = brandingProfiles(this.ws.view, this.s.userId).advisor;
    const fill: Record<string, string> = {};
    const want: [string, string | null | undefined][] = [['advisorName', `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()], ['agencyName', p.company], ['licenceNumber', p.licenceNumber], ['licenceAuthority', p.licenceAuthority], ['phone', p.phone || p.officePhone], ['email', p.email], ['website', p.website], ['address', p.address], ['country', p.country]];
    for (const [k, val] of want) if (val && !(mine as Record<string, unknown> | null)?.[k]) fill[k] = val;
    if (p.licenceExpiry && !mine?.licenceExpiry) fill.licenceExpiry = p.licenceExpiry;
    if (!Object.keys(fill).length) return;
    await this.ws.run((v, c) => saveBranding(v, { scope: 'advisor', userId: this.s.userId!, ...(mine ? { id: mine.id } : {}), ...fill }, c));
  }

  async signIn(url: string, input: { email: string; password: string; totp?: string; workspaceId?: string }): Promise<LoginResponse> {
    const u = platformUrl(url);
    const r = await new PlatformClient(u).login({ ...input, email: input.email.trim(), deviceId: this.deviceId, deviceLabel: deviceLabel() });
    if ('choose' in r) return r;
    await this.adopt(u, input.email.trim().toLowerCase(), r);
    return r;
  }
  /** Oct 2026 (idea 40): sign in with a passkey (Face ID / Touch ID / fingerprint) instead of the password — counts as
   *  both steps. `answer` asks the device (navigator.credentials.get); a person in several workspaces chooses one. */
  async signInWithPasskey(url: string, answer: (o: Awaited<ReturnType<PlatformClient['passkeyLoginOptions']>>) => Promise<Record<string, unknown>>, pick?: { choice: string; workspaceId: string }): Promise<LoginResponse & { choice?: string }> {
    const u = platformUrl(url);
    const c = new PlatformClient(u);
    const r = pick ? await c.passkeyLogin({ ...pick, deviceId: this.deviceId, deviceLabel: deviceLabel() })
      : await c.passkeyLogin({ credential: await answer(await c.passkeyLoginOptions()), deviceId: this.deviceId, deviceLabel: deviceLabel() });
    if ('choose' in r) return r;
    const email = await new PlatformClient(u, r.token).me().then((m) => m.profile?.email ?? '').catch(() => '');
    await this.adopt(u, email.toLowerCase(), r);
    return r;
  }
  async register(url: string, input: Omit<RegisterInput, 'deviceId'>): Promise<AccountSession> {
    const u = platformUrl(url);
    const r = await new PlatformClient(u).register({ ...input, email: input.email.trim(), deviceId: this.deviceId, deviceLabel: deviceLabel() });
    await this.adopt(u, input.email.trim().toLowerCase(), r);
    return r;
  }
  forgot(url: string, email: string) { return new PlatformClient(platformUrl(url)).forgot(email.trim()); }
  reset(url: string, token: string, password: string) { return new PlatformClient(platformUrl(url)).reset(token.trim(), password); }

  /** refresh profile, plan and licence from the platform; offline keeps the last verified licence */
  async refresh(): Promise<MeResult | null> {
    if (!this.signedIn) return null;
    try {
      const me = await this.client().me();
      if (PLATFORM_CONFIG.publicKey && !sameKey(PLATFORM_CONFIG.publicKey, me.publicKey)) throw new PlatformError(0, 'key_mismatch');
      await this.save({ licence: me.licence || null, profile: me.profile, email: me.profile?.email || this.s.email, plan: me.plan, subscription: me.subscription, mfaEnabled: me.mfaEnabled, role: me.role, workspaceName: me.workspaceName, checkedAt: new Date().toISOString(), lastError: null });
      await this.evaluate();
      return me;
    } catch (e) {
      const err = e as PlatformError;
      if (err.status === 401 && this.s.via === 'website' && this.memToken) { this.memToken = null; await this.save({ lastError: 'website_session_ended' }); }
      else if (err.status === 401) await this.save({ revoked: true, lastError: err.message });
      else await this.save({ lastError: err.message || 'network' });
      await this.evaluate();
      return null;
    }
  }
  /** calls that need the signed-in session */
  get api(): PlatformClient { if (!this.signedIn) throw new PlatformError(401, 'signed_out'); return this.client(); }
  async afterProfile(p: AccountProfile & { email: string }) { await this.save({ profile: p }); this.emit(); }
  async afterLicence(licence: string) { await this.save({ licence }); await this.evaluate(); }

  async signOut(opts: { keepSync?: boolean } = {}) {
    const token = this.token;
    try { if (this.signedIn) await this.client().logout(); } catch { /* offline: the server session expires on its own */ }
    if (this.s.via === 'website' && sameOriginPlatform()) { try { await new PlatformClient(sameOriginPlatform()!).webLogout(); } catch { /* the cookie expires on its own */ } }
    const sync = syncService();
    if (sync && !opts.keepSync && token && sync.sessionToken === token) await sync.unpair();
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    this.memToken = null;
    await this.save({ ...EMPTY, serverUrl: this.s.serverUrl, email: this.s.email, publicKey: this.s.publicKey, website: this.s.website ?? null, owner: this.hasLocalData() ? this.s.owner ?? null : null });
    await this.evaluate();
  }
  /** the account no longer exists (erased): forget the session; the data on this device stays */
  async forget() { await this.signOut(); }
}

let SERVICE: AccountService | null = null;
export const accountService = () => SERVICE;
export async function startAccountService(ws: Workspace) { SERVICE = await AccountService.start(ws); return SERVICE; }
